<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  ReviewImportMeta,
  ReviewImportPreviewRow,
  ReviewImportResponse,
  ReviewImportStatus,
} from '@shopexperts/legacy-api';
import { useReviewImport, extractMessage } from '@/app/composables/reviews/useReviewImport';
import ImportReviewsPickerDialog from './ImportReviewsPickerDialog.vue';
import ImportReviewsPreviewDialog from './ImportReviewsPreviewDialog.vue';

const props = defineProps<{
  expertId: number | string;
  expertName: string;
}>();

const emit = defineEmits<{
  imported: [response: ReviewImportResponse];
}>();

const open = defineModel<boolean>('open', { default: false });

type Step = 'pick' | 'preview';

const step = ref<Step>('pick');
const selectedFile = ref<File | null>(null);
const selectedStatus = ref<ReviewImportStatus>('pending');
const previewRows = ref<ReviewImportPreviewRow[]>([]);
const previewMeta = ref<ReviewImportMeta | null>(null);
const previewError = ref<string | null>(null);
const importError = ref<string | null>(null);

const pickerOpen = ref(false);
const previewOpen = ref(false);

const importer = useReviewImport(() => props.expertId);

const isPreviewLoading = computed(() => importer.previewMutation.isPending.value);
const isImporting = computed(() => importer.importMutation.isPending.value);

watch(open, (isOpen) => {
  if (isOpen) {
    resetState();
    step.value = 'pick';
    pickerOpen.value = true;
    previewOpen.value = false;
  } else {
    pickerOpen.value = false;
    previewOpen.value = false;
    resetState();
  }
});

watch(() => props.expertId, () => {
  if (open.value) open.value = false;
});

function resetState() {
  selectedFile.value = null;
  selectedStatus.value = 'pending';
  previewRows.value = [];
  previewMeta.value = null;
  previewError.value = null;
  importError.value = null;
  importer.reset();
}

function handlePickerClose() {
  if (open.value) open.value = false;
}

function handlePreviewClose() {
  if (open.value) open.value = false;
}

async function handleNext(file: File) {
  selectedFile.value = file;
  previewError.value = null;
  try {
    const response = await importer.previewFile(file, selectedStatus.value);
    previewRows.value = response.data;
    previewMeta.value = response.meta;
    step.value = 'preview';
    pickerOpen.value = false;
    previewOpen.value = true;
  } catch (err) {
    previewError.value = extractMessage(err);
  }
}

function handleBack() {
  importError.value = null;
  step.value = 'pick';
  previewOpen.value = false;
  pickerOpen.value = true;
}

function handleStatusChange(status: ReviewImportStatus) {
  selectedStatus.value = status;
}

async function handleConfirm() {
  if (!selectedFile.value) return;
  importError.value = null;
  try {
    const response = await importer.importFile(
      selectedFile.value,
      selectedStatus.value,
    );
    emit('imported', response);
    open.value = false;
  } catch (err) {
    importError.value = extractMessage(err);
  }
}
</script>

<template>
  <ImportReviewsPickerDialog
    v-model="pickerOpen"
    :expert-name="expertName"
    :initial-file="selectedFile"
    :loading="isPreviewLoading"
    :error-message="previewError"
    @next="handleNext"
    @close="handlePickerClose"
  />

  <ImportReviewsPreviewDialog
    v-model="previewOpen"
    :expert-name="expertName"
    :rows="previewRows"
    :meta="previewMeta"
    :file-name="selectedFile?.name"
    :importing="isImporting"
    :error-message="importError"
    :status="selectedStatus"
    @back="handleBack"
    @confirm="handleConfirm"
    @close="handlePreviewClose"
    @update:status="handleStatusChange"
  />
</template>
